import { useMemo } from 'react'
import type { EChartsCoreOption } from 'echarts/core'
import type { BenchmarkComparison } from '@greencandle/api-client'
import { useECharts } from './echarts'
import { useChartTheme } from './theme'

/**
 * FR-E5. The account's growth against a benchmark's, both starting at 100.
 *
 * <p>Two lines, so there is a legend: identity is never carried by colour alone (NFR-U2),
 * and the end of each line is labelled with what it came to.
 *
 * <p>What is plotted on the portfolio side is the time-weighted return, not market value.
 * The note under the chart says so, because the difference is the whole point and a reader
 * who assumes otherwise will read a deposit as a good month.
 */
export function BenchmarkCompare({
  comparison,
  dimmed,
}: {
  comparison: BenchmarkComparison
  dimmed?: boolean
}) {
  const theme = useChartTheme()

  const option = useMemo<EChartsCoreOption | null>(() => {
    if (comparison.series.length < 2) return null

    const dates = comparison.series.map((p) => p.date)
    const mine = comparison.series.map((p) => Number(p.portfolio))
    // null, not zero: a day the benchmark did not trade is a break in the line. Zero would
    // draw a cliff to the axis and read as the index having gone to nothing.
    const theirs = comparison.series.map((p) =>
      p.benchmark === null ? null : Number(p.benchmark),
    )

    const line = (name: string, data: (number | null)[], colour: string, dashed: boolean) => ({
      name,
      type: 'line' as const,
      data,
      showSymbol: false,
      connectNulls: false,
      lineStyle: { width: 2, color: colour, type: dashed ? ('dashed' as const) : ('solid' as const) },
      itemStyle: { color: colour },
      emphasis: { focus: 'series' as const },
      endLabel: {
        show: true,
        color: colour,
        fontSize: 11,
        formatter: (p: { value: number | null }) =>
          p.value === null ? '' : `${p.value >= 100 ? '+' : ''}${(p.value - 100).toFixed(1)}%`,
      },
    })

    return {
      animation: false,
      grid: { left: 8, right: 56, top: 28, bottom: 8, containLabel: true },
      legend: {
        top: 0,
        left: 0,
        itemWidth: 12,
        itemHeight: 2,
        textStyle: { color: theme.textMuted, fontSize: 11 },
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: theme.surface,
        borderColor: theme.axis,
        textStyle: { color: theme.text, fontSize: 12 },
        valueFormatter: (v: number | null) =>
          v === null ? 'no close' : `${(v - 100).toFixed(2)}%`,
      },
      xAxis: {
        type: 'category',
        data: dates,
        boundaryGap: false,
        axisLine: { lineStyle: { color: theme.axis, width: 1 } },
        axisLabel: { color: theme.textMuted, fontSize: 11, hideOverlap: true },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        scale: true,
        axisLabel: {
          color: theme.textMuted,
          fontSize: 11,
          // The axis is an index, but nobody thinks in index points. 100 is "level".
          formatter: (v: number) => `${v >= 100 ? '+' : ''}${(v - 100).toFixed(0)}%`,
        },
        splitLine: { lineStyle: { color: theme.grid, width: 1 } },
      },
      series: [
        line('This account', mine, theme.accent, false),
        line(comparison.symbol, theirs, theme.other, true),
      ],
    }
  }, [comparison, theme])

  const ref = useECharts(option)

  if (comparison.note) {
    return <p className="chart-note muted">{comparison.note}</p>
  }
  if (option === null) {
    return <p className="chart-note muted">Not enough valued days to draw a comparison yet.</p>
  }

  return (
    <>
      <div
        ref={ref}
        className={`chart-canvas${dimmed ? ' refetching' : ''}`}
        style={{ height: 260 }}
        role="img"
        aria-label={`Growth of this account against ${comparison.symbol}, both from 100`}
      />
      <p className="chart-note muted">
        Time-weighted: money paid in or taken out is removed, so only what the holdings did is
        plotted. Both lines start at 100 on {comparison.from}. {comparison.symbol} is price
        return, and so is this account&rsquo;s line — neither counts dividends received as cash.
      </p>
    </>
  )
}
